import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, ArrowRight, CheckCircle, Eye, EyeOff } from "lucide-react";
import { HIPAABadge } from "@/components/HIPAACompliance";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="reset-success">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-4"><HIPAABadge variant="pill" /></div>
          <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Password Reset!</h2>
            <p className="text-slate-500 text-sm mb-6">Your password has been reset successfully.</p>
            <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 w-full" onClick={() => navigate("/login")} data-testid="go-to-login-btn">
              Sign In <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-100 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-5 h-5 text-white" /></div>
          <span className="font-bold text-2xl tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
        </Link>
        <div className="flex justify-center mb-4"><HIPAABadge variant="pill" /></div>
        <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
          <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Set New Password</h1>
          <p className="text-slate-500 text-sm mb-6">Enter your new password below.</p>
          {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3 text-sm mb-4" data-testid="reset-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">New Password</Label>
              <div className="relative mt-1">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="rounded-xl pr-10" required data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Confirm Password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className="mt-1 rounded-xl" required data-testid="reset-confirm-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-base" data-testid="reset-submit-btn">
              {loading ? "Resetting..." : "Reset Password"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
